/**
 * 组件 props 与 store（propsStore）双向绑定工具
 * 从 storeProps 合并到 restProps，使 setProps(sessionId, id, { disabled: true }) 等能生效
 */

import {
  collectContextChainRootToLeaf,
  type DataSetInheritContextLike,
} from "./scopeChain";

const DEFAULT_SKIP_KEYS = ['value', 'validateStatus', 'help'] as const;

type ScopeCtxLike = {
  index?: number;
  parentIndex?: number;
  parentContext?: DataSetInheritContextLike | null;
} | null | undefined;

/**
 * 将 store 中的 props（propsStore[sessionId_props][id]）合并到组件 restProps
 * 跳过 value/help/validateStatus，避免覆盖组件内部管理的展示值
 */
export function mergeStoreProps<T extends Record<string, any>>(
  restProps: T,
  storeProps: Record<string, any> | null | undefined,
  options?: { skipKeys?: string[] }
): T {
  const skipKeys = options?.skipKeys ?? (DEFAULT_SKIP_KEYS as unknown as string[]);
  const out: Record<string, any> = { ...restProps };
  if (!storeProps || typeof storeProps !== 'object') return out as T;
  Object.keys(storeProps).forEach((key) => {
    if (skipKeys.includes(key)) return;
    const v = storeProps[key];
    if (v !== undefined && v !== null) out[key] = v;
  });
  return out as T;
}

/** 从 props 对象中剔除指定 key（用于避免自定义 prop 透传到 DOM） */
export function omitKeys<T extends Record<string, unknown>>(
  props: T,
  keys: readonly string[],
): Partial<T> {
  const out: Record<string, unknown> = { ...props };
  for (const key of keys) {
    delete out[key];
  }
  return out as Partial<T>;
}

/** 运行时自定义 prop，禁止透传到 antd / 原生 DOM */
export const AND_RUNTIME_DOM_SKIP = [
  'sessionId',
  'develop',
  /** 设计器 comp-defaultShow：由页面 visible/defaultShow 槽位消费，非 DOM 属性 */
  'defaultShow',
  /** AndButton 图标名，由组件解析为 antd icon，非 DOM 属性 */
  'iconName',
  'iconPlacement',
  'iconPosition',
  /** AndButton 文案，渲染为 children，非 DOM 属性 */
  'text',
  'tooltip',
  'dataSet',
  'dataSetField',
  'index',
  'clickAction',
  'changeAction',
  'searchAction',
  'selectAction',
  'deselectAction',
  'focusAction',
  'blurAction',
  'pressEnterAction',
  'clearAction',
  'stopClickPropagation',
  'dictCode',
  'formatType',
  'formatValue',
  'fallbackDisplay',
  'remoteUrl',
  'rules',
  'validateOnChange',
  'validateOnBlur',
  'showValidateHelp',
] as const;

/** 剔除运行时自定义 prop，避免 React DOM 警告 */
export function stripAndRuntimeProps<T extends Record<string, unknown>>(
  props: T,
  extraSkip?: readonly string[],
): Partial<T> {
  const keys = extraSkip
    ? [...AND_RUNTIME_DOM_SKIP, ...extraSkip]
    : AND_RUNTIME_DOM_SKIP;
  return omitKeys(props, keys);
}

/**
 * 生成 propsStore 作用域组件 ID。
 * - 叶节点同时有 parentIndex、index：与历史一致；二者相等时退化为 `id_${index}`。
 * - 多层 Provider（parentContext 链）：用各层 index 拼接（根→叶），至少两段时 `id_i_j_...`。
 * - 无 ctx：explicitIndex 或裸 id。
 */
export function deriveScopedComponentId(
  id: string | number | undefined | null,
  ctx?: ScopeCtxLike,
  explicitIndex?: number
): string | null {
  if (id == null || id === '') return null;

  const chain = collectContextChainRootToLeaf(ctx as DataSetInheritContextLike | null | undefined);
  const leaf = chain.length > 0 ? chain[chain.length - 1] : undefined;

  const parentIndex =
    leaf && typeof leaf.parentIndex === "number" ? leaf.parentIndex : undefined;
  const contextIndex =
    leaf && typeof leaf.index === "number" ? leaf.index : undefined;

  if (
    parentIndex !== undefined &&
    Number.isFinite(parentIndex) &&
    contextIndex !== undefined &&
    Number.isFinite(contextIndex)
  ) {
    if (parentIndex !== contextIndex) {
      return `${String(id)}_${parentIndex}_${contextIndex}`;
    }
    return `${String(id)}_${parentIndex}`;
  }

  if (chain.length >= 2) {
    const parts = chain
      .map((n) => n.index)
      .filter((i): i is number => typeof i === "number" && Number.isFinite(i));
    if (parts.length >= 2) {
      return `${String(id)}_${parts.join("_")}`;
    }
  }

  const scopeIndex =
    parentIndex !== undefined && Number.isFinite(parentIndex)
      ? parentIndex
      : contextIndex !== undefined && Number.isFinite(contextIndex)
        ? contextIndex
        : typeof explicitIndex === "number"
          ? explicitIndex
          : undefined;
  if (scopeIndex !== undefined && Number.isFinite(scopeIndex)) {
    return `${String(id)}_${scopeIndex}`;
  }
  return String(id);
}
